import React, { useState, useEffect } from 'react';

interface FeedbackPanelProps {
  onSubmit?: (feedback: { like: boolean | null; favorite: boolean; comment: string }) => void;
  isMobile?: boolean;
}

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ onSubmit, isMobile }) => {
  const [like, setLike] = useState<boolean | null>(null);
  const [favorite, setFavorite] = useState(false);
  const [comment, setComment] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hideSuccess, setHideSuccess] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => setHideSuccess(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  const handleFeedbackClick = (type: 'like' | 'dislike' | 'favorite') => {
    if (type === 'like') setLike(true);
    if (type === 'dislike') setLike(false);
    if (type === 'favorite') setFavorite(!favorite);
    setShowInput(true);
  };

  const handleClosePanel = () => {
    setClosed(true);
    setShowInput(false);
    setSubmitted(false);
    setHideSuccess(false);
    setLike(null);
    setFavorite(false);
    setComment('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setShowInput(false); // Always close modal on submit
    if (onSubmit) {
      onSubmit({ like, favorite, comment });
    }
  };

  if (submitted && !hideSuccess) {
    return (
      <div
        style={{
          background: 'rgba(247, 231, 198, 0.96)', // matches bg-yellow-100
          color: '#7c5b00',
          borderRadius: 16,
          padding: '18px 24px',
          margin: '18px auto',
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 16,
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          maxWidth: 340,
        }}
      >
        Your feedback just made our AI robot smile! ✨
      </div>
    );
  }

  if (closed) return null;

  return (
    <div className={`mt-4 p-4 rounded-xl border border-gray-200 shadow-xl w-full`} style={{ background: 'rgba(255,255,255,1)' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600 text-center flex-1">How was this recommendation?</p>
        <button
          aria-label="Close feedback panel"
          onClick={handleClosePanel}
          style={{ marginLeft: 8, color: '#aaa', fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}
        >
          &times;
        </button>
      </div>
      <div className="flex justify-center items-center gap-3 mb-3">
        <button
          onClick={() => handleFeedbackClick('like')}
          className={`flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            like === true
              ? 'bg-green-100 text-green-700 border-2 border-green-300'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <span className="text-lg">👍</span>
          <span className="hidden sm:inline">Like</span>
        </button>
        <button
          onClick={() => handleFeedbackClick('dislike')}
          className={`flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            like === false
              ? 'bg-red-100 text-red-700 border-2 border-red-300'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <span className="text-lg">👎</span>
          <span className="hidden sm:inline">Dislike</span>
        </button>
        <button
          onClick={() => handleFeedbackClick('favorite')}
          className={`flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            favorite
              ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <span className="text-lg">⭐</span>
          <span className="hidden sm:inline">Favorite</span>
        </button>
      </div>
      {showInput && (
        <>
          <textarea
            className="mt-2 w-full rounded border border-gray-200 p-2 text-sm focus:ring-2 focus:ring-pink-200 focus:outline-none resize-none"
            rows={2}
            placeholder="We appreciate your feedback! What did you enjoy or what could we do better?"
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
          <div className="flex justify-center mt-2">
            <button
              onClick={handleSubmit}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 bg-pink-500 text-white hover:bg-pink-600 transform hover:scale-105`}
            >
              Submit Feedback
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FeedbackPanel; 